import React, { useEffect, useMemo, useState } from "react";
import { fetchToday } from "../lib/api";
import { QuickScoreModal } from "./QuickScoreModal";

/**
 * TodayTab — single-glance "what should I do right now?" panel for the
 * dashboard center column. One round-trip to /api/me/today fans out into:
 *   - next match card (with QUICK SCORE button if I'm captain)
 *   - badges: pending invites, unread DMs, unread mentions, league chat
 *   - tip line — explains the most useful next action
 *
 * Refreshed in real-time by parent (via useUserHub events).
 */
export function TodayTab({ onOpenLeague, onOpenInbox, onOpenDms, refreshKey = 0, onToast, registerReload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoreMatch, setScoreMatch] = useState(null);

  const reload = () => {
    setLoading(true);
    fetchToday()
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [refreshKey]);
  useEffect(() => { registerReload?.(reload); /* eslint-disable-next-line */ }, []);

  const m = data?.next_match;
  const inboxTotal = data?.inbox?.total || 0;
  const dmUnread = data?.dm_unread || 0;
  const mentionUnread = data?.mention_unread || 0;
  const leagueUnreadTotal = data?.league_unread_total || 0;

  const matchTime = useMemo(() => {
    if (!m?.scheduled_at) return null;
    try { return new Date(m.scheduled_at); } catch { return null; }
  }, [m?.scheduled_at]);

  return (
    <div className="glass rounded-3xl p-5 border border-[#14b8a6]/30"
         data-testid="dash-today-block">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-bolt mr-1" />TODAY · LIVE
        </p>
        <span className="flex items-center gap-1.5 text-[9px] tracking-widest text-[#14b8a6]/80">
          <span className="w-1.5 h-1.5 rounded-full bg-[#14b8a6] animate-pulse" />
          REAL-TIME
        </span>
      </div>

      {loading && !data ? (
        <p className="text-[12px] text-white/40 py-3 text-center">Loading your day…</p>
      ) : (
        <>
          {data?.tip && (
            <p className="text-[12px] text-white/75 mb-4 italic" data-testid="dash-today-tip">
              <i className="fas fa-lightbulb text-[#14b8a6] mr-1.5" />{data.tip}
            </p>
          )}

          {/* NEXT MATCH CARD */}
          {m ? (
            <div data-testid="dash-today-nextmatch"
                 className={`rounded-2xl p-3 mb-4 border ${m.is_live ? "bg-[#14b8a6]/10 border-[#14b8a6]/50" : "bg-black/30 border-[#14b8a6]/30"}`}>
              <div className="flex items-center gap-2 mb-1">
                <p className={`text-[9px] tracking-[3px] font-bold ${m.is_live ? "text-[#14b8a6]" : "text-[#14b8a6]"}`}>
                  {m.is_live ? <><i className="fas fa-circle text-[8px] mr-1 animate-pulse" />LIVE NOW</> :
                    m.status === "pending" ? <><i className="far fa-clock mr-1" />AWAITING ACCEPT</> :
                    <><i className="far fa-calendar mr-1" />NEXT MATCH</>}
                </p>
                {m.minutes_until > 0 && !m.is_live && (
                  <span className="text-[10px] text-white/55">
                    in {m.minutes_until < 60 ? `${m.minutes_until}m` :
                         m.minutes_until < 24 * 60 ? `${Math.round(m.minutes_until / 60)}h` :
                         `${Math.round(m.minutes_until / (60 * 24))}d`}
                  </span>
                )}
              </div>
              <p className="text-sm font-bold text-white truncate">
                <span className="text-[#14b8a6]">{m.from_team?.name || "Team A"}</span>
                <span className="text-white/40 mx-1.5">vs</span>
                <span className="text-[#14b8a6]">{m.to_team?.name || "Team B"}</span>
              </p>
              <p className="text-[10px] text-[#a3a3ac] truncate">
                <i className="fas fa-trophy mr-1" />{m.league?.name || "League"}
                {matchTime && (
                  <>
                    <span className="text-white/30 mx-1.5">·</span>
                    {matchTime.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}
                  </>
                )}
              </p>
              <div className="mt-2 flex gap-2 flex-wrap">
                <button type="button"
                        onClick={() => onOpenLeague?.(m.league?.league_id)}
                        data-testid="dash-today-open-league"
                        className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost">
                  <i className="fas fa-arrow-right mr-1" />OPEN LEAGUE
                </button>
                {m.can_score && (
                  <button type="button"
                          onClick={() => setScoreMatch(m)}
                          data-testid="dash-today-quick-score"
                          className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6] text-black font-bold hover:brightness-110">
                    <i className="fas fa-bolt mr-1" />QUICK SCORE
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p data-testid="dash-today-no-match"
               className="text-[12px] text-white/45 text-center py-3 bg-black/20 rounded-2xl mb-4">
              No upcoming matches. Tap SCHEDULE below to challenge a team.
            </p>
          )}

          {/* BADGE GRID — quick counts */}
          <div className="grid grid-cols-4 gap-2" data-testid="dash-today-badges">
            <BadgeTile color="violet" icon="fa-envelope" label="INVITES" count={inboxTotal}
                       onClick={onOpenInbox} testId="today-badge-inbox" />
            <BadgeTile color="green"  icon="fa-comment-dots" label="DMS" count={dmUnread}
                       onClick={onOpenDms} testId="today-badge-dms" />
            <BadgeTile color="cyan"   icon="fa-at" label="MENTIONS" count={mentionUnread}
                       testId="today-badge-mentions" />
            <BadgeTile color="pink"   icon="fa-hashtag" label="LEAGUE" count={leagueUnreadTotal}
                       testId="today-badge-league" />
          </div>
        </>
      )}

      {scoreMatch && (
        <QuickScoreModal
          match={scoreMatch}
          onClose={() => setScoreMatch(null)}
          onScored={() => { setScoreMatch(null); reload(); }}
          onToast={onToast}
        />
      )}
    </div>
  );
}

function BadgeTile({ color, icon, label, count, onClick, testId }) {
  const palette = {
    violet: { fg: "text-[#14b8a6]", bg: "bg-[#14b8a6]/15", border: "border-[#14b8a6]/40", hover: "hover:bg-[#14b8a6]/25" },
    green:  { fg: "text-[#14b8a6]", bg: "bg-[#14b8a6]/15", border: "border-[#14b8a6]/40", hover: "hover:bg-[#14b8a6]/25" },
    cyan:   { fg: "text-[#14b8a6]", bg: "bg-[#14b8a6]/15", border: "border-[#14b8a6]/40", hover: "hover:bg-[#14b8a6]/25" },
    pink:   { fg: "text-[#14b8a6]", bg: "bg-[#14b8a6]/15", border: "border-[#14b8a6]/40", hover: "hover:bg-[#14b8a6]/25" },
  }[color];
  const C = onClick ? "button" : "div";
  return (
    <C type={onClick ? "button" : undefined} onClick={onClick}
       data-testid={testId}
       className={`flex flex-col items-center p-2.5 rounded-xl border ${palette.bg} ${palette.border} ${onClick ? palette.hover + " cursor-pointer" : ""} transition-colors`}>
      <i className={`fas ${icon} ${palette.fg} text-base mb-1`} />
      <p className={`font-display text-xl leading-none ${count > 0 ? "text-white" : "text-white/40"} tabular-nums`}>
        {count > 99 ? "99+" : count}
      </p>
      <p className={`text-[8px] tracking-[2px] mt-1 ${palette.fg}`}>{label}</p>
    </C>
  );
}

export default TodayTab;
